/**
 * Kalender-Export: .ics-Datei und Google-Calendar-URL.
 *
 * Bewusst client-seitig: kein Server-Roundtrip, keine zusaetzliche API.
 * Description enthaelt den Teilnehmer-Link (NIE den Admin-Link, auch wenn
 * der aktuelle Viewer Admin ist) — der Kalendereintrag wandert oft per
 * Share weiter, da darf der Admin-Link nicht mit raus.
 */

export type CalendarEvent = {
  /** Eindeutige UID, z. B. "<shortcut>@brings-mit.com". */
  uid: string;
  title: string;
  description: string;
  location: string | null;
  /** ISO-8601 mit oder ohne Offset. */
  startIso: string;
  /** Standard-Dauer in Minuten, wenn kein End-Zeitpunkt bekannt. */
  durationMinutes?: number;
  /** Teilnehmer-Link, der in URL-Property + Description landet. */
  url: string;
};

/** Macht einen ISO-String zu einem ICS-konformen "YYYYMMDDTHHMMSSZ" (UTC). */
function toIcsUtcStamp(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

/** Maskiert ICS-Spezialzeichen in TEXT-Property-Werten. */
function escIcs(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/** ICS-Zeilen sollen <= 75 Oktetts sein, lange Zeilen werden gefaltet. */
function foldLine(line: string): string {
  if (line.length <= 75) return line;
  const chunks: string[] = [];
  let i = 0;
  while (i < line.length) {
    chunks.push((i === 0 ? "" : " ") + line.slice(i, i + 73));
    i += 73;
  }
  return chunks.join("\r\n");
}

export function buildIcs(event: CalendarEvent): string {
  const dtStart = toIcsUtcStamp(event.startIso);
  const durationMin = event.durationMinutes ?? 180; // Default 3h
  const dtEnd = toIcsUtcStamp(
    new Date(new Date(event.startIso).getTime() + durationMin * 60_000).toISOString(),
  );
  const dtStamp = toIcsUtcStamp(new Date().toISOString());

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//brings-mit.com//Calendar Export 1.0//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${escIcs(event.uid)}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${escIcs(event.title)}`,
    `DESCRIPTION:${escIcs(event.description)}`,
    `URL:${escIcs(event.url)}`,
    ...(event.location ? [`LOCATION:${escIcs(event.location)}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  return lines.map(foldLine).join("\r\n");
}

/** Erzeugt einen `data:`-Blob-Link und triggert den Download. */
export function downloadIcs(event: CalendarEvent, filename = "event.ics"): void {
  const ics = buildIcs(event);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Google-Calendar-Pre-Filled-URL. Datum im YYYYMMDDTHHMMSSZ-Format. */
export function googleCalendarUrl(event: CalendarEvent): string {
  const start = toIcsUtcStamp(event.startIso);
  const durationMin = event.durationMinutes ?? 180;
  const end = toIcsUtcStamp(
    new Date(new Date(event.startIso).getTime() + durationMin * 60_000).toISOString(),
  );
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    details: event.description,
    dates: `${start}/${end}`,
  });
  if (event.location) params.set("location", event.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Outlook (live.com) Quick-Add-URL. */
export function outlookCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    body: event.description,
    startdt: event.startIso,
    enddt: new Date(
      new Date(event.startIso).getTime() + (event.durationMinutes ?? 180) * 60_000,
    ).toISOString(),
  });
  if (event.location) params.set("location", event.location);
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Helper: baut die CalendarEvent-Datenstruktur fuer ein brings-mit-Event.
 * Description enthaelt: {beschreibung}\n\n📋 Mitbringsel-Liste: {guestUrl}
 */
export function buildEventCalendar(opts: {
  shortcut: string;
  title: string;
  startIso: string;
  location: string | null;
  description: string | null;
  guestUrl: string;
  /** lokaler Text "Mitbringsel-Liste:" o. ä., aus i18n. */
  listLabel: string;
}): CalendarEvent {
  const desc = [
    opts.description?.trim() || "",
    `${opts.listLabel} ${opts.guestUrl}`,
  ]
    .filter(Boolean)
    .join("\n\n");

  return {
    uid: `${opts.shortcut}@brings-mit.com`,
    title: opts.title,
    description: desc,
    location: opts.location?.trim() || null,
    startIso: opts.startIso,
    url: opts.guestUrl,
  };
}
